import { create } from 'zustand';
import managementService from '../api/managementService';
import logger from '../utils/logger';

// ponytail: Zustand store replacing the ManagementContext boilerplate
export const useManagementStore = create((set, get) => ({
  members: [],
  stats: null,
  loading: true,
  error: null,

  fetchMembers: async (params = {}) => {
    try {
      set({ loading: true, error: null });
      const response = await managementService.getManagementMembers(params);
      if (response.status === 'success') {
        set({ members: response.data.members || [] });
      }
    } catch (err) {
      logger.error('Error fetching management members:', err);
      set({ error: err.response?.data?.message || 'Failed to fetch management members' });
    } finally {
      set({ loading: false });
    }
  },

  fetchStats: async () => {
    try {
      const response = await managementService.getManagementStats();
      if (response.status === 'success') {
        set({ stats: response.data });
      }
    } catch (err) {
      logger.error('Error fetching management stats:', err);
    }
  },

  addMember: async (formData) => {
    try {
      const response = await managementService.createManagementMember(formData);
      if (response.status === 'success' && response.data?.member) {
        set((state) => ({ members: [...state.members, response.data.member] }));
        return response.data.member;
      }
    } catch (err) {
      logger.error('Error creating management member:', err);
      throw err;
    }
  },

  updateMember: async (id, formData) => {
    try {
      const response = await managementService.updateManagementMember(id, formData);
      if (response.status === 'success' && response.data?.member) {
        set((state) => ({
          members: state.members.map((m) => (m._id === id ? response.data.member : m)),
        }));
        return response.data.member;
      }
    } catch (err) {
      logger.error('Error updating management member:', err);
      throw err;
    }
  },

  deleteMember: async (id) => {
    try {
      await managementService.deleteManagementMember(id);
      set((state) => ({ members: state.members.filter((m) => m._id !== id) }));
    } catch (err) {
      logger.error('Error deleting management member:', err);
      throw err;
    }
  },

  bulkDeleteMembers: async (ids) => {
    try {
      await managementService.bulkDeleteManagementMembers(ids);
      set((state) => ({ members: state.members.filter((m) => !ids.includes(m._id)) }));
    } catch (err) {
      logger.error('Error bulk deleting management members:', err);
      throw err;
    }
  },

  getMembersByCategory: (category) => {
    return get().members.filter((m) => m.category === category && m.isActive);
  },

  getActiveMembers: () => {
    return get().members.filter((m) => m.isActive);
  },

  getFeaturedMembers: () => {
    return get().members.filter((m) => m.featured && m.isActive);
  },
}));

// ponytail: init driven by PublicDataLoader — not here
